import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { X, Download, Share } from "lucide-react";

/**
 * PWA install prompt.
 * - Android/Chrome: shows the native beforeinstallprompt banner
 * - iOS Safari: shows instructions to Add to Home Screen
 */
export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [show, setShow] = useState(false);
  const [iosHint, setIosHint] = useState(false);

  useEffect(() => {
    // Already installed?
    if (window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone) {
      return;
    }
    if (localStorage.getItem("ikz_pwa_dismissed") === "1") return;

    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    const isSafari = /^((?!chrome|android|crios|fxios).)*safari/i.test(navigator.userAgent);

    if (isIOS && isSafari) {
      const t = setTimeout(() => setIosHint(true), 4000);
      return () => clearTimeout(t);
    }

    const handler = (e) => {
      e.preventDefault();
      setDeferred(e);
      setShow(true);
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    const { outcome } = await deferred.userChoice;
    setShow(false);
    setDeferred(null);
    if (outcome === "accepted") localStorage.setItem("ikz_pwa_dismissed", "1");
  };

  const dismiss = () => {
    setShow(false);
    setIosHint(false);
    localStorage.setItem("ikz_pwa_dismissed", "1");
  };

  if (!show && !iosHint) return null;

  return (
    <div
      data-testid="pwa-install-banner"
      className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 w-[calc(100%-2rem)] max-w-md bg-white border-2 border-[#2E5A36] rounded-2xl shadow-2xl p-4 flex items-center gap-3 animate-in fade-in slide-in-from-bottom-4"
    >
      <img
        src="/icons/icon-192.png"
        alt="İkizler"
        className="w-14 h-14 rounded-2xl border border-[#E8E2D5]"
      />
      <div className="flex-1 min-w-0">
        <p className="font-heading font-extrabold text-[#1B2A22] leading-tight">
          İkizler'i telefonuna ekle
        </p>
        {iosHint ? (
          <p className="text-xs text-[#1B2A22]/70 mt-1 leading-snug">
            Paylaş <Share className="inline w-3 h-3 mx-0.5" /> → <b>Ana Ekrana Ekle</b>
          </p>
        ) : (
          <p className="text-xs text-[#1B2A22]/70 mt-1">
            Uygulama gibi tek dokunuşla aç, hızlı sipariş ver.
          </p>
        )}
      </div>
      <div className="flex flex-col gap-1 shrink-0">
        {!iosHint && (
          <Button
            data-testid="pwa-install-btn"
            onClick={install}
            className="bg-[#2E5A36] hover:bg-[#1B2A22] text-[#FAF7F2] rounded-full h-9 text-xs px-3 font-heading font-bold"
          >
            <Download className="w-3.5 h-3.5 mr-1" /> Ekle
          </Button>
        )}
        <button
          data-testid="pwa-dismiss-btn"
          onClick={dismiss}
          className="text-[#1B2A22]/40 hover:text-[#1B2A22] text-xs"
        >
          <X className="w-3.5 h-3.5 inline" />
        </button>
      </div>
    </div>
  );
}
